import { Transaction } from 'models/transaction'
import { Button, List, message, Modal, Select, Input, Form } from 'antd'
import { useAppState } from 'state'
import { StopTransaction, ErrorTransaction, CancelPosPayment } from 'services/data-provider/transaction'
import { renderFormatMessage } from 'helpers/intl'
import { useState } from 'react'
import { ListCardWrapper } from 'atom/list'

const TransactionPanelAction: React.FC<{ transaction: Transaction, reloadTransactions: () => void }> = ({ transaction, reloadTransactions }) => {
  const { currentUser } = useAppState()
  const [loading, setLoading] = useState(false)
  const [disabled, setDisabled] = useState(false)

  const [errorLoading, setErrorLoading] = useState(false)
  const [errorDisabled, setErrorDisabled] = useState(false)
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false)
  const [form] = Form.useForm()

  const [cancelPaymentLoading, setCancelPaymentLoading] = useState(false)
  const [cancelPaymentDisabled, setCancelPaymentDisabled] = useState(false)

  const handleStopCharging = async () => {
    try {
      setLoading(true)
      // disable the button
      setDisabled(true)
      await StopTransaction(transaction)
      message.success('Charging session stopped successfully, Please wait for the transaction to be updated')
      // add a delay here to give some time to the transaction to be updated
      setTimeout(() => {
        reloadTransactions()
        setDisabled(false)
      }, 5000)
    } catch (error) {
      console.error('Failed to stop charging:', error)
      message.error('Failed to stop charging')
      setDisabled(false)
    } finally {
      setLoading(false)
    }
  }

  const handleErrorTransaction = async (values: any) => {
    try {
      setErrorLoading(true)
      setErrorDisabled(true)
      setIsErrorModalVisible(false)
      
      // Combine reason and custom reason if "Other" is selected
      const errorReason = values.customReason || values.reason
      
      await ErrorTransaction(transaction, errorReason)
      
      message.success('Error transaction successfully. Please wait for the transaction to be updated')
      
      setTimeout(() => {
        reloadTransactions()
        setErrorDisabled(false)
      }, 5000)
    } catch (error) {
      message.error('Failed to error transaction')
      setErrorDisabled(false)
    } finally {
      setErrorLoading(false)
    }
  }

  const showErrorModal = () => {
    setIsErrorModalVisible(true)
    form.resetFields()
  }

  const hideErrorModal = () => {
    setIsErrorModalVisible(false)
    form.resetFields()
  }

  // This button is used by Payter transaction only
  const handleCancelPayment = async () => {
    try {
      setCancelPaymentLoading(true)
      setCancelPaymentDisabled(true)
      
      await CancelPosPayment(transaction)
      
      message.success('Payment cancelled successfully. Please wait for the transaction to be updated')
      setTimeout(() => {
        reloadTransactions()
        setCancelPaymentDisabled(false)
      }, 5000)
    } catch (error) {
      message.error('Failed to cancel payment')
      setCancelPaymentDisabled(false)
    } finally {
      setCancelPaymentLoading(false)
    }
  }

  const errorReasons = [
    'Payment processing failed',
    'Charging station malfunction',
    'Connector failure',
    'Network/connectivity issue',
    'Unauthorized transaction',
    'Customer dispute',
    'Other (please specify)'
  ]

  // onlye allow admin / whitelabel admin to stop charging
  if (currentUser?.role !== 'admin') {
    return null
  }

  return (
    <ListCardWrapper style={{ paddingTop: '12px' }}>
        {/* This button is used by Payter transaction only */}
        {transaction.payterInfo && 
         transaction.payterInfo.sessionState?.toLowerCase() === 'authorized' && (
          <List.Item style={{ borderBottom: 'none', paddingBottom: '0px' }}>
               <Button
                   type="default"
                   disabled={cancelPaymentDisabled}
                   loading={cancelPaymentLoading}
                   onClick={handleCancelPayment}
                   style={{ width: '100%' }}
                   >
                   {renderFormatMessage('dashboard.transactionPage.cancel-payment', 'Cancel Payment')}
               </Button>
          </List.Item>
        )}

        {['no_reservation', 'checked_in', 'ongoing'].includes(transaction.currentState) && (
          <List.Item style={{ borderBottom: 'none', paddingBottom: '0px' }}>
              <Button
                  type="primary"
                  danger
                  disabled={disabled}
                  loading={loading}
                  onClick={handleStopCharging}
                  style={{ width: '100%' }}
                  >
                  {renderFormatMessage('dashboard.transactionPage.stop-charging-session', 'Stop Charging Session')}
              </Button>
          </List.Item>
        )}
        {transaction.currentState !== 'errored' && (
          <List.Item style={{ borderBottom: 'none' }}>
              <Button
                  danger
                  disabled={errorDisabled}
                  loading={errorLoading}
                  onClick={showErrorModal}
                  style={{ width: '100%' }}
                  >
                  {renderFormatMessage('dashboard.transactionPage.error-transaction', 'Error Transaction')}
              </Button>
          </List.Item>
        )}
        
        <Modal
          title="Confirm Error Transaction"
          visible={isErrorModalVisible}
          onCancel={hideErrorModal}
          footer={null}
          width={500}
          centered
        >
          <div style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.5' }}>
            Are you sure you want to error transaction <strong>#{transaction.id}</strong>?
            <br />
            This action cannot be undone.
          </div>
          
          <div style={{ marginBottom: '16px', fontWeight: 'bold', fontSize: '14px' }}>
            Please select a reason for this error:
          </div>
          
          <Form
            form={form}
            onFinish={handleErrorTransaction}
            layout="vertical"
          >
            <Form.Item
              name="reason"
              rules={[{ required: true, message: 'Please select a reason' }]}
            >
              <Select placeholder="Select a Reason">
                {errorReasons.map((reason) => (
                  <Select.Option key={reason} value={reason}>
                    {reason}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.reason !== currentValues.reason
              }
            >
              {({ getFieldValue }) => {
                const reason = getFieldValue('reason')
                return reason === 'Other (please specify)' ? (
                  <Form.Item
                    name="customReason"
                    label="Type a reason:"
                    rules={[
                      { required: true, message: 'Please specify a reason' },
                      { min: 1, message: 'At least 1 character is required' }
                    ]}
                  >
                    <Input placeholder="Specify a new reason" />
                  </Form.Item>
                ) : null
              }}
            </Form.Item>
            
            <Form.Item style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <Button onClick={hideErrorModal}>
                  Cancel
                </Button>
                <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
                  {({ getFieldValue }) => {
                    const reason = getFieldValue('reason')
                    const customReason = getFieldValue('customReason')
                    const isDisabled = !reason || (reason === 'Other (please specify)' && !customReason)
                    
                    return (
                      <Button
                        danger={!isDisabled}
                        htmlType="submit"
                        disabled={isDisabled}
                      >
                        Error Transaction
                      </Button>
                    )
                  }}
                </Form.Item>
              </div>
            </Form.Item>
          </Form>
        </Modal>
    </ListCardWrapper>
  )
}

export default TransactionPanelAction